/**
 * Dokumentum-állapotgép: betöltés, piszkozat, autosave, ütközéskezelés.
 * A UI ehhez iratkozik fel — így a mentési logika egy helyen, tesztelhetően él.
 */
import { CONFIG } from '../config';
import {
  getContent,
  getHeadRevisionId,
  getMetadata,
  saveContent,
  saveAsCopy,
  type FileMeta,
} from '../drive/api';

export type SaveState = 'idle' | 'dirty' | 'saving' | 'saved' | 'error' | 'conflict';

export interface DocState {
  meta: FileMeta | null;
  content: string;
  savedContent: string;
  saveState: SaveState;
  lastSavedAt: Date | null;
  error: string | null;
  readOnly: boolean;
}

type Listener = (state: DocState) => void;

const draftKey = (id: string) => `bitsquad-md:draft:${id}`;

export class DocumentStore {
  private state: DocState = {
    meta: null,
    content: '',
    savedContent: '',
    saveState: 'idle',
    lastSavedAt: null,
    error: null,
    readOnly: false,
  };

  private listeners = new Set<Listener>();
  private idleTimer: number | null = null;
  private maxTimer: number | null = null;
  private baseRevisionId: string | undefined;
  private resourceKey: string | undefined;

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    fn(this.state);
    return () => this.listeners.delete(fn);
  }

  get(): DocState {
    return this.state;
  }

  private set(patch: Partial<DocState>): void {
    this.state = { ...this.state, ...patch };
    for (const fn of this.listeners) fn(this.state);
  }

  /** Drive-ból betöltés; ha van helyi piszkozat, felajánljuk visszaállításra. */
  async load(fileId: string, resourceKey?: string): Promise<{ draft: string | null }> {
    this.resourceKey = resourceKey;
    const meta = await getMetadata(fileId, resourceKey);
    const size = Number(meta.size ?? '0');
    const readOnly = meta.capabilities?.canEdit === false || size > CONFIG.hugeFileBytes;
    const content = await getContent(fileId, resourceKey);
    this.baseRevisionId = meta.headRevisionId;
    this.set({
      meta,
      content,
      savedContent: content,
      readOnly,
      saveState: 'idle',
      error: null,
      lastSavedAt: null,
    });

    const draft = localStorage.getItem(draftKey(fileId));
    return { draft: draft && draft !== content ? draft : null };
  }

  /** Új, még nem létező dokumentum betöltése a store-ba. */
  adopt(meta: FileMeta, content: string): void {
    this.baseRevisionId = meta.headRevisionId;
    this.set({
      meta,
      content,
      savedContent: content,
      readOnly: false,
      saveState: 'idle',
      error: null,
    });
  }

  /** Szerkesztőből érkező változás. */
  update(content: string): void {
    if (content === this.state.content) return;
    const dirty = content !== this.state.savedContent;
    this.set({ content, saveState: dirty ? 'dirty' : 'idle' });
    if (this.state.meta && dirty) {
      try {
        localStorage.setItem(draftKey(this.state.meta.id), content);
      } catch {
        /* tele a tároló – nem kritikus */
      }
    }
    if (dirty) this.scheduleAutosave();
  }

  private scheduleAutosave(): void {
    if (this.state.readOnly) return;
    if (this.idleTimer) window.clearTimeout(this.idleTimer);
    this.idleTimer = window.setTimeout(() => void this.save(), CONFIG.autosaveIdleMs);
    if (!this.maxTimer) {
      this.maxTimer = window.setTimeout(() => void this.save(), CONFIG.autosaveMaxMs);
    }
  }

  private clearTimers(): void {
    if (this.idleTimer) window.clearTimeout(this.idleTimer);
    if (this.maxTimer) window.clearTimeout(this.maxTimer);
    this.idleTimer = null;
    this.maxTimer = null;
  }

  /**
   * Mentés a Drive-ba. Előbb ellenőrzi, hogy a szerveren lévő revízió az-e,
   * amiről a betöltés óta tudunk — ha nem, `conflict` állapotba megy.
   */
  async save(force = false): Promise<void> {
    const { meta, content, savedContent, readOnly, saveState } = this.state;
    if (!meta || readOnly || saveState === 'saving') return;
    if (content === savedContent && !force) return;

    this.clearTimers();
    this.set({ saveState: 'saving', error: null });

    try {
      if (!force) {
        const current = await getHeadRevisionId(meta.id, this.resourceKey);
        if (this.baseRevisionId && current && current !== this.baseRevisionId) {
          this.set({ saveState: 'conflict' });
          return;
        }
      }
      const updated = await saveContent(meta.id, content, this.resourceKey);
      this.baseRevisionId = updated.headRevisionId;
      localStorage.removeItem(draftKey(meta.id));
      this.set({
        meta: { ...meta, ...updated },
        savedContent: content,
        saveState: 'saved',
        lastSavedAt: new Date(),
      });
    } catch (err) {
      this.set({ saveState: 'error', error: err instanceof Error ? err.message : String(err) });
    }
  }

  /** Ütközés feloldása: felülírás. */
  async overwrite(): Promise<void> {
    await this.save(true);
  }

  /** Ütközés feloldása: mentés új fájlba. */
  async saveCopy(): Promise<FileMeta | null> {
    const { meta, content } = this.state;
    if (!meta) return null;
    this.set({ saveState: 'saving' });
    try {
      const copy = await saveAsCopy(meta, content);
      localStorage.removeItem(draftKey(meta.id));
      this.set({ saveState: 'saved', savedContent: content, lastSavedAt: new Date() });
      return copy;
    } catch (err) {
      this.set({ saveState: 'error', error: err instanceof Error ? err.message : String(err) });
      return null;
    }
  }

  /** Ütközés feloldása: a szerveren lévő verzió betöltése (helyi módosítás eldobása). */
  async reloadFromDrive(): Promise<void> {
    const { meta } = this.state;
    if (!meta) return;
    const fresh = await getMetadata(meta.id, this.resourceKey);
    const content = await getContent(meta.id, this.resourceKey);
    this.baseRevisionId = fresh.headRevisionId;
    localStorage.removeItem(draftKey(meta.id));
    this.set({ meta: fresh, content, savedContent: content, saveState: 'idle', error: null });
  }

  isDirty(): boolean {
    return this.state.content !== this.state.savedContent;
  }
}
